import type { AnswersByQuestion, ContentItem } from "./layout/documentLayout";
import { chartRowIndexes } from "./question-types/chartRows";

// Required-answer check before the agency submits a document from the review, ported from the
// classic form's `validateArray` / `validateAnswer` (editPatientDocModalCtrl.js). Pure so it is
// unit-tested in node (ci/document-review-session.test.cjs).

/** Types the classic form never blocks a submit on: no answer, or the patient's live records. */
const NEVER_REQUIRED = new Set<ContentItem["itemType"]>([
  "bigHeader",
  "smallHeader",
  "editor",
  "image",
  "vbpItem",
  "medicationProfile",
  "medicationProfileItem",
  "icd",
  "icdChart",
  "icdCodes",
  "sectionedIcdCodes",
  "finalIcdCodes",
  "medicalDiagnosis",
  "bloodPressure",
  "weight",
  "patientPhysician",
]);

export interface MissingRequiredAnswer {
  question: ContentItem;
  /** -1 outside charts; the chart row of a chart cell. */
  chartRow: number;
}

function childrenOf(question: ContentItem): ContentItem[] {
  return question.columns?.[0] ?? [];
}

function answerAt(answers: AnswersByQuestion, questionId: number, chartRow: number): unknown {
  return answers.get(questionId)?.find((answer) => answer.chartRow === chartRow)?.answer;
}

/** A conditional child shows when its parent's answer is (or, for a checkbox, includes) the value. */
function isShown(question: ContentItem, parentValue: unknown): boolean {
  if (question.showIfParentEquals !== true) {
    return true;
  }
  if (Array.isArray(parentValue)) {
    return (parentValue as unknown[]).includes(question.ifParentEquals);
  }
  return parentValue === question.ifParentEquals;
}

function isAnswered(question: ContentItem, value: unknown): boolean {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  switch (question.itemType) {
    case "check":
      return Array.isArray(value) && value.length > 0;
    case "bodyDrawing":
      // The classic form wants every view drawn
      return Array.isArray(value) && value.length > 0 && !value.includes("");
    default:
      return !(typeof value === "number" && Number.isNaN(value));
  }
}

function collect(
  questions: ContentItem[],
  answers: AnswersByQuestion,
  parentValue: unknown,
  chartRow: number,
  missing: MissingRequiredAnswer[]
): void {
  for (const question of questions) {
    if (isShown(question, parentValue)) {
      collectQuestion(question, answers, chartRow, missing);
    }
  }
}

function collectQuestion(
  question: ContentItem,
  answers: AnswersByQuestion,
  chartRow: number,
  missing: MissingRequiredAnswer[]
): void {
  // A plan of care is never checked, its duties included (classic form)
  if (question.itemType === "POC") {
    return;
  }
  if (question.itemType === "chart") {
    // Every cell of every row, shown or not (the classic form does not apply conditions here)
    for (const row of chartRowIndexes(question, answers)) {
      for (const cell of childrenOf(question)) {
        collectQuestion(cell, answers, row, missing);
      }
    }
    return;
  }
  const value = answerAt(answers, question.id, chartRow);
  if (
    question.isRequired === true &&
    !NEVER_REQUIRED.has(question.itemType) &&
    !isAnswered(question, value)
  ) {
    missing.push({ question, chartRow });
  }
  collect(childrenOf(question), answers, value, chartRow, missing);
}

/** Required questions without an answer, in document order. */
export function missingRequiredAnswers(
  content: ContentItem[],
  answers: AnswersByQuestion
): MissingRequiredAnswer[] {
  const missing: MissingRequiredAnswer[] = [];
  collect(content, answers, null, -1, missing);
  return missing;
}
